import { chakra, Link } from '@chakra-ui/react'
import CircleFor from './CircleFor'
import Starfield from './assets/starfield.svg'
import Earth from './Earth'

export default ({ width = 1280, height = 960 }) => {
  const Background = () => (
    <foreignObject
      x={-width / 2} y={-height / 2}
      {...{ width, height }}
    >
      <object
        {...{ width, height }}
        data={Starfield}
        aria-label="background"
      />
    </foreignObject>
  )

  const Text = chakra('text')

  const PlayerBase = ({
    username, displayName = username, color = 'purple'
  }, idx) => (
    <Link
      key={displayName}
      className="player"
      href={
        `//my.metagame.wtf/player/${username}`
      }
      _hover={{
        path: { stroke: 'red' },
        text: { fill: 'yellow' },
      }}
    >
      <path
        d='m-7,5 h14 l-7,-15 z'
        fill="transparent" stroke={color}
        strokeWidth={2}
      />
      <Text
        dy={19}
        fontSize={10}
        textAnchor="middle"
        fill="white"
        stroke="black"
        strokeWidth={1}
        paintOrder="stroke"
      >
        {displayName}
      </Text>
    </Link>
  )
  const Player = ({ ...overrides }) => (
    ({ ...props }) => (
      <PlayerBase {...Object.assign(
        {}, props, overrides
      )}/>
    )
  )

  const TroubledCircle = ({ r = 50, ...props }) => (
    <g className='troubled'>
      <defs>
        <filter id="heatwave">
          <feTurbulence
            baseFrequency="0.05"
            numOctaves="5"
            type="fractalNoise"
            result="tumult"
          />
          <feDisplacementMap
            scale="25"
            result="displaced"
            xChannelSelector="R"
            in="SourceGraphic"
            in2="tumult"
          >
            <animate
              attributeName="scale"
              values="25; 15; 20; 35; 20"
              begin="troubled.mouseover"
              dur="1s"
              end="troubled.mouseout"
              repeatCount="indefinite"
            />
          </feDisplacementMap>
          <feComposite
            in="SourceGraphic"
            operator="atop"
            in2="displaced"
          />
        </filter>
      </defs>
      <circle
        id="troubled"
        {...{ r, ...props }}
        filter="url(#heatwave)"
      >
        <animateTransform
          attributeName="transform"
          type="rotate"
          values="0; 360"
          begin="mouseover"
          dur="10s"
          end="mouseout"
          repeatCount="indefinite"
        />
      </circle>
    </g>
  )

  const HazyCircle = ({
    r = 50, fill = 'blue', opacity = 0.75, ...props
  }) => (
    <g className="hazy">
      <defs>
        <radialGradient id="haze">
          <stop offset="0%" stopColor="transparent"/>
          <stop offset="80%" stopColor="transparent"/>
          <stop offset="87%" stopColor={fill} stopOpacity={opacity}/>
          <stop offset="93%" stopColor={fill} stopOpacity={opacity}/>
          <stop offset="100%" stopColor="transparent"/>
        </radialGradient>
      </defs>
      <circle {...{ r, ...props }} fill="url(#haze)"/>
    </g>
  ) 

  const RaidBase = ({
    name = null, sides = 6, r = 25, ...props
  }) => {
    const arc = 2 * Math.PI / sides
    const points = (
      [...new Array(sides)]
      .map((_, i) => ({
        x: r * Math.cos(i * arc),
        y: r * Math.sin(i * arc),
      }))
    )
    return (
      <g className="raid">
        <path
          d={
            `m${points[0].x},${points[0].y}`
            + points.slice(1).map(
              p => `L${p.x},${p.y}`
            )
            + 'z'
          }
          {...props}
        />
        <Text
          fontSize={18}
          textAnchor="middle"
          fill="white"
          stroke="black"
          strokeWidth={2}
          paintOrder="stroke"
        >
          {name.split("\n").map((part, i) => (
            <tspan
              key={i}
              x={0} y={r * 1.75 + 18 * i}
            >
              {part}
            </tspan>
          ))}
        </Text>
      </g>
    )
  }
  const Raid = ({ ...overrides }) => (
    ({ ...props }) => (
      <RaidBase {...Object.assign(
        {}, props, overrides
      )}/>
    )
  )

  return (
    <chakra.svg
      viewBox={[
        -width / 2, -height / 2,
        width, height,
      ].join(' ')}
      preserveAspectRatio="xMidYMid meet"
      _first={{ '*': {
        transitionProperty: 'fill stroke',
        transitionDelay: '0.5s',
      } }}
    >
      <Background/>
      <Link href="//metagame.wtf">
        <Earth size={275} land="green" water="blue"/>
      </Link>
      <CircleFor
        id="inner"
        source="inner-circle.json"
        style={{
          fill: 'none',
          stroke: 'lightblue',
          strokeWidth: 2,
          strokeDasharray: '7 5',
          strokeLinecap: 'round',
        }}
        r={100}
        template={Player({ color: 'purple' })}
      />
      <CircleFor
        id="middle"
        source="middle-circle.json"
        style={{
          fill: 'none',
          stroke: 'purple',
          strokeWidth: 2,
          strokeDasharray: '1 4',
          strokeLinecap: 'round',
        }}
        r={150}
        template={Player({ color: 'lime' })}
      />
      <TroubledCircle
        fill="none" stroke="red" strokeWidth={5}
        r={220}
      />
      <HazyCircle
        r={390} fill="green"
        pointerEvents="none"
      />
      <CircleFor
        id="raids"
        source="raids.json"
        style={{
          fill: 'none',
          stroke: 'none',
        }}
        r={350}
        template={Raid({
          style: {
            fill: 'yellow',
            stroke: 'orange',
            strokeWidth: 4,
            strokeDasharray: '3 6',
          }
        })}
      />
    </chakra.svg>
  )
}